import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Input,
  InputLabel,
  FormHelperText,
  Typography,
  IconButton,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import formConfig from "./createaccountformconfig.json";
import { useNavigate } from "react-router-dom";

const CreateAccountForm = ({ setShowAppBar }) => {
  const { header, description, fields, buttons } = formConfig.columns[0];
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  React.useLayoutEffect(() => {
    setShowAppBar(false);
    return () => {
      setShowAppBar(true);
    };
  }, [setShowAppBar]);

  const handleInputChange = (fieldName, value) => {
    setFormData((prevData) => ({ ...prevData, [fieldName]: value }));
    // Validate field on input change

    if (errors[fieldName]) {
      console.log(errors[fieldName]);
      validateField(fieldName, value);
    }
  };

  const validateField = (fieldName, value) => {
    const field = fields.find((f) => f.name === fieldName);
    let error = "";

    // Required field validation
    if (field.Required && !value) {
      error = field.ErrorMsg || "This field is required";
    }
    // Length validation
    else if (field.Length && value.length > field.Length) {
      error = `Must be less than ${field.Length} characters`;
    }
    // Email validation
    else if (field.type === "email" && !/\S+@\S+\.\S+/.test(value)) {
      error = "Invalid email address";
    }
    // Password-specific rules
    else if (field.name === "password") {
      if (field.rules) {
        if (field.rules.numberRequired && !/\d/.test(value)) {
          error = "Password must contain at least one number";
        }
        if (
          !error &&
          field.rules.special &&
          !new RegExp(`[${field.rules.special}]`).test(value)
        ) {
          error = `Password must contain one of these special characters: ${field.rules.special}`;
        }
      }
    }

    setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: error }));
    return error === "";
  };

  const handleSubmit = (button) => {
    if (button.label === "continue") {
      // Validate all fields before submission
      const isValid = fields.every((field) =>
        validateField(field.name, formData[field.name])
      );

      if (isValid) {
        console.log("Form Submitted", formData);
      }
    }
    if (button.label === "back") {
      handleBack();
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleLoginClick = () => {
    navigate("/loginpage");
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      {/* Top Navigation Buttons */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "space-between",
          padding: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIosNewIcon />}
          onClick={handleBack}
          sx={{
            textTransform: "uppercase",
          }}
        >
          Back
        </Button>

        <Button
          onClick={handleLoginClick}
          sx={{
            textTransform: "uppercase",
          }}
        >
          Login
        </Button>
      </Box>

      {/* Main Form Container */}
      <Box
        sx={{
          paddingTop: 8,
          maxWidth: 320, // Reduced form width
          margin: "0 auto",
        }}
      >
        <Typography
          variant="h6"
          align="center"
          sx={{ fontWeight: "bold", fontSize: "1.1rem", marginBottom: 2 }}
        >
          {header}
        </Typography>
        <Typography
          variant="body2"
          align="center"
          sx={{ fontSize: "0.85rem", marginBottom: 3 }}
        >
          {description}
        </Typography>

        <form noValidate>
          {fields.map((field) => (
            <FormControl
              key={field.name}
              fullWidth
              variant="standard"
              sx={{
                marginBottom: 2.5,
                "& .MuiInput-underline:before": {
                  borderBottom: "1px solid #ccc",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottom: "1px solid #000",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "2px solid #000",
                },
              }}
              error={Boolean(errors[field.name])}
            >
              <InputLabel
                htmlFor={field.name}
                required={field.Required}
                sx={{
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  fontWeight: "bold",
                }}
              >
                {field.label}
              </InputLabel>
              <Input
                id={field.name}
                type={
                  field.type === "password" && !showPassword
                    ? "password"
                    : "text"
                }
                value={formData[field.name] || ""}
                onChange={(e) => handleInputChange(field.name, e.target.value)}
                endAdornment={
                  field.type === "password" ? (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ) : null
                }
                sx={{
                  "& input": {
                    padding: "8px 0",
                  },
                }}
              />
              {errors[field.name] && (
                <FormHelperText>{errors[field.name]}</FormHelperText>
              )}
            </FormControl>
          ))}

          <Typography
            variant="body2"
            align="center"
            sx={{
              fontSize: "0.75rem",
              marginBottom: 2,
            }}
          >
            By creating an account, you agree to our Terms of Service and
            Privacy Policy.
          </Typography>

          <Box
            sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
          >
            {buttons.map((button) => (
              <Button
                key={button.label}
                variant="contained"
                fullWidth
                onClick={() => handleSubmit(button)}
                sx={{
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                }}
              >
                {button.label}
              </Button>
            ))}
          </Box>
          <Typography
            variant="body2"
            align="center"
            sx={{
              fontSize: "0.65rem",
              marginTop: 2,
            }}
          >
            Secure Login with reCAPTCHA subject to Google Terms & Privacy
          </Typography>
        </form>
      </Box>
    </Box>
  );
};

export default CreateAccountForm;
